import { IBaseCategory, ILocation, LeaflyCategory } from '@getgreenline/homi-shared'
import { observable, computed, action } from 'mobx'

export enum Integrators {
  LEAFLY = 'Leafly',
  WEEDMAP = 'Weedmap',
}

export type TIntegrationCategoryName<T extends Integrators> = T extends Integrators.LEAFLY
  ? LeaflyCategory
  : string

export interface IIntegrationCategoryItem<T extends Integrators> {
  id: string
  name: TIntegrationCategoryName<T>
}

type TIntegrationAPIKey<T extends Integrators> = T extends Integrators.LEAFLY | Integrators.WEEDMAP
  ? string | null
  : number | null

export class IntegrationCategory<T extends Integrators> {
  greenlineCategory: IBaseCategory

  @observable
  integrationCategory: IIntegrationCategoryItem<T> | null

  @observable
  updatedIntegrationCategory: IIntegrationCategoryItem<T> | null

  @observable
  mappedAvailableCategories: Map<string, IIntegrationCategoryItem<T>> = new Map()

  @computed
  get availableCategories() {
    return [...this.mappedAvailableCategories.values()]
  }

  @observable
  isDisabled = false

  constructor(
    greenlineCategory: IBaseCategory,
    integrationCategory: IIntegrationCategoryItem<T> | null,
  ) {
    this.greenlineCategory = greenlineCategory
    this.integrationCategory = integrationCategory
    this.updatedIntegrationCategory = integrationCategory
  }

  @action
  setIntegrationCategory = (integrationCategory: IIntegrationCategoryItem<T> | null) => {
    this.updatedIntegrationCategory = integrationCategory
  }

  @action
  syncIntegrationCategory = () => {
    this.integrationCategory = this.updatedIntegrationCategory
  }

  @action
  setAvailableCategories = (availableCategories: IIntegrationCategoryItem<T>[]) => {
    const mappedAvailableCategories = new Map<string, IIntegrationCategoryItem<T>>()

    availableCategories.forEach((category) => {
      mappedAvailableCategories.set(category.id, category)
    })

    this.mappedAvailableCategories = mappedAvailableCategories
  }

  @action
  setIsDisabled = (isDisabled: boolean) => {
    this.isDisabled = isDisabled
  }

  @computed
  get isUpdated(): boolean {
    return this.integrationCategory?.id !== this.updatedIntegrationCategory?.id
  }
}

export class IntegrationLocAPIKey<T extends Integrators> {
  location: ILocation

  apiKey: TIntegrationAPIKey<T>

  @observable
  updatedApiKey: TIntegrationAPIKey<T>

  @observable
  isEditable = false

  @observable
  isUpdating = false

  @observable
  canSave = true

  constructor(location: ILocation, apiKey: TIntegrationAPIKey<T>) {
    this.location = location
    this.apiKey = apiKey
    this.updatedApiKey = apiKey
  }

  @action
  setIsEditable = (isEditable: boolean) => {
    this.isEditable = isEditable
  }

  @action
  setIsUpdating = (isUpdating: boolean) => {
    this.isUpdating = isUpdating
  }

  @action
  setUpdatedApiKey = (apiKey: TIntegrationAPIKey<T>) => {
    this.updatedApiKey =
      typeof apiKey === 'string' ? (apiKey.trim() as TIntegrationAPIKey<T>) : apiKey
  }

  @action
  setCanSave = (canSave: boolean) => {
    this.canSave = canSave
  }

  @computed
  get isUpdated(): boolean {
    return this.apiKey !== this.updatedApiKey
  }
}

export class IntegrationStore<T extends Integrators> {
  integrationName: T

  @observable
  integrationCategories: IntegrationCategory<T>[] = []

  @observable
  locationApiKeys: IntegrationLocAPIKey<T>[] = []

  constructor(integrationName: T) {
    this.integrationName = integrationName
  }

  @action
  setLocationApiKeys = (locationApiKeys: IntegrationLocAPIKey<T>[]) => {
    this.locationApiKeys = locationApiKeys
  }

  @action
  setIntegrationCategories = (integrationCategories: IntegrationCategory<T>[]) => {
    this.integrationCategories = integrationCategories
  }

  @computed
  get anyUpdated(): boolean {
    return this.integrationCategories.some((item) => item.isUpdated)
  }

  @computed
  get updatableCatagories(): IntegrationCategory<T>[] {
    return this.integrationCategories.filter((categoryItem) => categoryItem.isUpdated)
  }

  @computed
  get updatableApiKeys(): IntegrationLocAPIKey<T>[] {
    return this.locationApiKeys.filter((locationItem) => locationItem.isUpdated)
  }

  @computed
  get hasNoApiKey(): boolean {
    return this.locationApiKeys.every((locationApiKey) => locationApiKey.apiKey === null)
  }
}
